// ============================================================
// HISTORICAL IMPORT MODULE
// src/HistoricalImport.js
// Opening balances + Daily sales upload from other system
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const fmt = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

// ── SHARED STYLES ─────────────────────────────────────────────
const S = {
  pageHeader: {display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:28,paddingBottom:20,borderBottom:"1px solid var(--paper3)"},
  pageTitle:  {fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"},
  subtitle:   {fontSize:12,color:"var(--ink3)",marginTop:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  btnDanger:  {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--danger-l)",background:"transparent",color:"var(--danger)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  statCard:   (c) => ({background:"#fff",borderRadius:10,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",borderLeft:`3px solid ${c}`}),
  th:         (r) => ({padding:"10px 16px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"12px 16px",fontSize:13,color:"var(--ink)"},
  input:      {padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%"},
  label:      {fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:5},
  overlay:    {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
  section:    {background:"#fff",borderRadius:10,padding:24,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",marginBottom:20},
  sectionTitle:{fontFamily:"var(--serif)",fontSize:17,fontWeight:300,marginBottom:4,color:"var(--ink)"},
  sectionSub: {fontSize:12,color:"var(--ink3)",marginBottom:16},
};

// ── HELPERS ───────────────────────────────────────────────────
export function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers:[], rows:[] };
  const delimiters = ["\t",",",";","|"];
  const delim = delimiters.find(d => (lines[0].split(d).length) > 2) || ",";
  const splitLine = (line) => {
    const result=[]; let cur=""; let inQ=false;
    for (let i=0;i<line.length;i++) {
      const ch=line[i];
      if (ch==='"') inQ=!inQ;
      else if (ch===delim&&!inQ) { result.push(cur.trim()); cur=""; }
      else cur+=ch;
    }
    result.push(cur.trim());
    return result;
  };
  const headers = splitLine(lines[0]).map(h=>h.replace(/^"|"$/g,"").trim());
  const rows = lines.slice(1).map(line=>{
    const vals = splitLine(line);
    const obj={};
    headers.forEach((h,i)=>{ obj[h]=(vals[i]||"").replace(/^"|"$/g,"").trim(); });
    return obj;
  }).filter(r=>Object.values(r).some(v=>v));
  return { headers, rows };
}

export function parseDate(str) {
  if (!str) return null;
  str = str.trim().split(" ")[0];
  // Try DD/MM/YYYY
  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(str)) {
    const [d,m,y] = str.split(/[/-]/);
    return `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
  }
  // Try YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // Try DD MMM YYYY
  const months={jan:"01",feb:"02",mar:"03",apr:"04",may:"05",jun:"06",jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12"};
  const parts = str.split(/[\s/-]/);
  if (parts.length===3 && isNaN(parts[1])) {
    const m = months[parts[1].toLowerCase().slice(0,3)];
    if (m) return `${parts[2]}-${m}-${parts[0].padStart(2,"0")}`;
  }
  return null;
}

// Exact name match only (case-insensitive, edges trimmed) — no fuzzy matching,
// since near-names like "SHRAVANTH" / "SHRAVANTH ARTISAN" are different
// customers and must never be merged. Anything that doesn't match exactly is
// left for the user to resolve manually. Returns a lookup: name -> customer|null.
export function makeExactMatcher(customers) {
  const norm = (n) => (n||"").toLowerCase().trim();
  const byName = new Map();
  customers.forEach(c => { const k = norm(c.name); if (!byName.has(k)) byName.set(k, c); });
  return (name) => byName.get(norm(name)) || null;
}

export function fuzzyMatch(name, customers) {
  if (!name) return null;
  const clean = (s) => s.toLowerCase().trim()
    .replace(/[&./-]/g," ")  // normalize special chars to space
    .replace(/\s+/g," ")         // collapse multiple spaces
    .trim();

  const n = clean(name);

  // 1. Exact match (case insensitive)
  let match = customers.find(c => clean(c.name) === n);
  if (match) return match;

  // 2. Exact match ignoring spaces and special chars
  const strip = (s) => clean(s).replace(/\s/g,"");
  match = customers.find(c => strip(c.name) === strip(n));
  if (match) return match;

  // 3. One contains the other (works for short names and abbreviations)
  match = customers.find(c => {
    const cn = clean(c.name);
    return cn === n || cn.startsWith(n+" ") || n.startsWith(cn+" ") ||
           cn.includes(" "+n+" ") || n.includes(" "+cn+" ");
  });
  if (match) return match;

  // 4. All words in recon name appear in customer name
  const words = n.split(" ").filter(w => w.length > 1);
  if (words.length >= 2) {
    match = customers.find(c => {
      const cn = clean(c.name);
      return words.every(w => cn.includes(w));
    });
    if (match) return match;
  }

  // 5. For single short words — exact word match only
  if (words.length === 1 && words[0].length <= 6) {
    match = customers.find(c => {
      const cn = clean(c.name);
      return cn === words[0] ||
             cn.startsWith(words[0]+" ") ||
             cn.endsWith(" "+words[0]);
    });
    if (match) return match;
  }

  // 6. Starts with match — recon name starts with customer name or vice versa
  match = customers.find(c => {
    const cn = clean(c.name);
    return n.startsWith(cn) || cn.startsWith(n);
  });
  if (match) return match;

  return null;
}
// ── BULK OPENING BALANCE UPLOAD ───────────────────────────────
// ── QUICK-ADD CUSTOMER (opened from an unresolved-name row during upload) ──
function QuickAddCustomerModal({ prefillName, onClose, onCreated, toast }) {
  const [name, setName]   = useState(prefillName||"");
  const [type, setType]   = useState("counter");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast("Customer name is required.","error");
    setSaving(true);
    try {
      const { data, error } = await supabase.from("customers")
        .insert({ name:name.trim(), type, phone:phone||null, is_active:true })
        .select().single();
      if (error) throw error;
      toast("Customer created.");
      onCreated(data);
    } catch(e) { toast("Error: "+e.message,"error"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{...S.overlay, zIndex:400}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:420}}>
        <div style={{padding:"20px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={S.pageTitle}>New customer</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"16px 24px 24px"}}>
          <label style={{...S.label,display:"block",marginBottom:5}}>Customer name *</label>
          <input value={name} onChange={e=>setName(e.target.value)} autoFocus style={{...S.input,marginBottom:14}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Type</label>
              <select value={type} onChange={e=>setType(e.target.value)} style={S.input}>
                <option value="counter">Counter</option>
                <option value="outstation">Outstation</option>
                <option value="tpf">TPF</option>
                <option value="prv">PRV</option>
              </select>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Phone</label>
              <input value={phone} onChange={e=>setPhone(e.target.value)} style={S.input}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":"💾 Create & use"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Groups unmatched preview rows by their raw name (deduped) for one-click
// resolution instead of requiring the file be re-uploaded from scratch.
function groupUnresolvedByName(rows, nameField) {
  const map = new Map();
  rows.forEach(r => {
    const name = r[nameField];
    if (!name) return;
    if (!map.has(name)) map.set(name, { name, count:0 });
    map.get(name).count++;
  });
  return [...map.values()];
}

function UnresolvedCustomersPanel({ groups, customers, onResolve, onCustomerCreated, toast }) {
  const [pickers, setPickers] = useState({});
  const [addFor, setAddFor] = useState(null);
  if (!groups.length) return null;
  return (
    <div style={{marginBottom:16,padding:"14px 16px",background:"var(--warn-l)",border:"1px solid var(--warn)",borderRadius:8}}>
      <div style={{fontSize:13,fontWeight:500,color:"var(--warn)",marginBottom:10}}>
        ⚠ {groups.length} customer name{groups.length!==1?"s":""} in this file could not be matched. Resolve each once — every row with that name updates together.
      </div>
      {groups.map(g => (
        <div key={g.name} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderTop:"1px solid rgba(0,0,0,0.06)"}}>
          <div style={{flex:"0 0 220px",fontSize:13,fontWeight:500}}>{g.name} <span style={{color:"var(--ink3)",fontWeight:400}}>({g.count} row{g.count!==1?"s":""})</span></div>
          <div style={{flex:1,maxWidth:260,position:"relative"}}>
            <input value={pickers[g.name]||""} onChange={e=>setPickers(p=>({...p,[g.name]:e.target.value}))}
              placeholder="Search customer…" style={{...S.input,fontSize:12}}/>
            {(pickers[g.name]||"")!=="" && (
              <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:50,maxHeight:180,overflowY:"auto",marginTop:2}}>
                {matchItems(customers, pickers[g.name], c=>c.name, 50).list.map(c=>(
                  <div key={c.id} style={{padding:"7px 10px",fontSize:12,cursor:"pointer",borderBottom:"1px solid var(--paper2)"}}
                    onMouseDown={()=>{onResolve(g.name,c);setPickers(p=>({...p,[g.name]:""}));}}>{c.name}</div>
                ))}
              </div>
            )}
          </div>
          <button style={S.btnSm} onClick={()=>setAddFor(g)}>+ Add customer</button>
        </div>
      ))}
      {addFor && (
        <QuickAddCustomerModal
          prefillName={addFor.name}
          onClose={()=>setAddFor(null)}
          toast={toast}
          onCreated={(cust)=>{ onCustomerCreated(cust); onResolve(addFor.name,cust); setAddFor(null); }}
        />
      )}
    </div>
  );
}

function BulkOpeningBalanceModal({ onClose, onSave, toast }) {
  const [step, setStep]       = useState(1); // 1=upload, 2=map, 3=preview, 4=result
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0,10));
  const [headers, setHeaders] = useState([]);
  const [rows, setRows]       = useState([]);
  const [mapping, setMapping] = useState({ name:"", sales:"", receipts:"" });
  const [preview, setPreview] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [result, setResult]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const fileRef = useRef(null);

useEffect(() => {
  const loadAll = async () => {
    let all = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const { data, error } = await supabase.from("customers")
        .select("id,name,phone")
        .eq("is_active", true)
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      all = [...all, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    setCustomers(all);
  };
  loadAll();
}, []);

  const parseFile = async (file) => {
    let text = "";
    if (file.name.endsWith(".xlsx")||file.name.endsWith(".xls")) {
      if (!window.XLSX) {
        await new Promise((res,rej)=>{
          const s=document.createElement("script");
          s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
          s.onload=res;s.onerror=rej;document.head.appendChild(s);
        });
      }
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(new Uint8Array(buf),{type:"array"});
      const ws = wb.Sheets[wb.SheetNames[0]];
      text = window.XLSX.utils.sheet_to_csv(ws);
    } else {
      text = await file.text();
    }
    const { headers: hdrs, rows: dataRows } = parseCSV(text);
    setHeaders(hdrs);
    setRows(dataRows);
    // Auto-map
const find = (...kw) => hdrs.find(h=>kw.some(k=>h.toLowerCase().includes(k)))||"";
setMapping({
  name:     find("customer","name"),  // "customer" before "name" to avoid S.No
  sales:    find("sale","debit"),     // "sale" before "amount" to avoid S.No amount
  receipts: find("receipt","received","credit","paid"),
});
    setStep(2);
  };

  const buildPreview = () => {
    if (!mapping.name)     return alert("Please map the Customer name column.");
    if (!mapping.sales && !mapping.receipts) return alert("Please map at least Sales or Receipts column.");
    const matchCustomer = makeExactMatcher(customers);
    const p = rows.map(row => {
      const name     = (row[mapping.name]||"").trim();
      const sales    = parseFloat((row[mapping.sales]||"0").replace(/[,₹\s]/g,""))||0;
      const receipts = parseFloat((row[mapping.receipts]||"0").replace(/[,₹\s]/g,""))||0;
      const customer = matchCustomer(name);
// Auto-set customer type from file if available
const typeCol = Object.keys(row).find(k=>k.toLowerCase()==="type");
const fileType = typeCol ? row[typeCol].toLowerCase().trim() : null;
const normalizedType = fileType==="counter"?"counter":fileType==="outstation"?"outstation":fileType==="tpf"?"tpf":fileType==="prv"?"prv":null;

return {
  name, sales, receipts,
  fileType: normalizedType,
  customer,
        matchedName: customer?.name||null,
        customerId:  customer?.id||null,
        balance:     sales - receipts,
        valid:       !!name,
        noMatch:     !customer && !!name,
      };
    }).filter(r => r.name);
    setPreview(p);
    setStep(3);
  };

  const resolveByName = (name, cust) => {
    setPreview(p => p.map(r => r.name===name
      ? { ...r, customer:cust, matchedName:cust.name, customerId:cust.id, noMatch:false }
      : r));
  };

  const upload = async () => {
    if (!asOfDate) return alert("Please set an as-of date.");
    setSaving(true);
    let upserted=0, skipped=0, errors=[];
    const seenCustomers = new Set();
    for (const row of preview.filter(r=>r.valid&&r.customerId)) {
      try {
// Guard: two file rows resolving to the same customer would silently
// overwrite each other — keep the first and report the rest.
if (seenCustomers.has(row.customerId)) throw new Error("another row in the file already maps to this customer — skipped");
seenCustomers.add(row.customerId);
const {error} = await supabase.from("customer_opening_balances").upsert({
  customer_id:      row.customerId,
  as_of_date:       asOfDate,
  opening_sales:    row.sales,
  opening_receipts: row.receipts,
  notes:            "Bulk upload",
  updated_at:       new Date().toISOString(),
},{onConflict:"customer_id"});
if (error) throw error;
// Update customer type if provided in file
if (row.fileType) {
  await supabase.from("customers").update({type:row.fileType}).eq("id",row.customerId);
}
upserted++;
      } catch(e) { errors.push(`${row.name}: ${e.message}`); }
    }
    skipped = preview.filter(r=>r.valid&&!r.customerId).length;
    setSaving(false);
    setResult({upserted, skipped, errors,
      unmatchedRows: preview.filter(r=>r.valid&&!r.customerId)});
    setStep(4);
  };

  const validCount    = preview.filter(r=>r.valid&&r.customerId).length;
  const unmatchedCount = preview.filter(r=>r.noMatch).length;
  const totalSales    = preview.filter(r=>r.customerId).reduce((s,r)=>s+r.sales,0);
  const totalReceipts = preview.filter(r=>r.customerId).reduce((s,r)=>s+r.receipts,0);

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:820,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Bulk opening balance upload</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>
              {["","Upload file","Map columns","Preview","Done"][step]}
            </div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>

        <div style={{padding:"20px 28px 28px"}}>

          {/* Step 1 — Upload */}
          {step===1&&(
            <div>
              {/* As-of date — always visible */}
              <div style={{marginBottom:20,padding:"16px",background:"var(--info-l)",borderRadius:8}}>
                <label style={S.label}>As of date * — the date this historical data applies to</label>
                <input type="date" value={asOfDate} onChange={e=>setAsOfDate(e.target.value)}
                  style={{...S.input,maxWidth:220}}/>
                <div style={{fontSize:11,color:"var(--info)",marginTop:4}}>
                  This is the cut-off date for your migration. All balances will be recorded as of this date.
                </div>
              </div>

              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:13,color:"var(--ink3)"}}>
                Prepare your Excel/CSV with columns: <strong>Customer name · Sale amount · Received amount</strong>
              </div>

              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>Click to select Excel or CSV file</div>
                <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>Drag and drop also works</div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}}
                  onChange={e=>parseFile(e.target.files[0])}/>
              </div>

              <div style={{marginTop:14,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                <strong>Sample format:</strong><br/>
                Customer Name &nbsp;|&nbsp; Sale Amount &nbsp;|&nbsp; Received Amount<br/>
                Navrang Lab Mangalore &nbsp;|&nbsp; 125000 &nbsp;|&nbsp; 90000<br/>
                Satish Yelahanka &nbsp;|&nbsp; 48000 &nbsp;|&nbsp; 48000
              </div>
            </div>
          )}

          {/* Step 2 — Map columns */}
          {step===2&&(
            <div>
              <div style={{marginBottom:14,padding:"10px 14px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                Detected {rows.length} rows · Columns: {headers.join(" · ")}
              </div>

              {/* As-of date editable here too */}
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--info-l)",borderRadius:8,display:"flex",alignItems:"center",gap:16}}>
                <label style={{...S.label,marginBottom:0,whiteSpace:"nowrap"}}>As of date *</label>
                <input type="date" value={asOfDate} onChange={e=>setAsOfDate(e.target.value)}
                  style={{...S.input,maxWidth:180}}/>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14}}>
                {[
                  {k:"name",     label:"Customer name column *"},
                  {k:"sales",    label:"Sale amount column *"},
                  {k:"receipts", label:"Received amount column"},
                ].map(f=>(
                  <div key={f.k}>
                    <label style={S.label}>{f.label}</label>
                    <select value={mapping[f.k]||""} onChange={e=>setMapping(m=>({...m,[f.k]:e.target.value}))}
                      style={S.input}>
                      <option value="">— Not mapped —</option>
                      {headers.map(h=><option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between",marginTop:20,paddingTop:16,borderTop:"1px solid var(--paper2)"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Back</button>
                <button style={S.btnPrimary} onClick={buildPreview}>Preview →</button>
              </div>
            </div>
          )}

          {/* Step 3 — Preview */}
          {step===3&&(
            <div>
              {/* Summary */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
                {[
                  {label:"Ready to upload", value:validCount,      color:"var(--accent)"},
                  {label:"No match",        value:unmatchedCount,  color:"var(--warn)"},
                  {label:"Total sales",     value:fmt(totalSales), color:"var(--info)"},
                  {label:"Total receipts",  value:fmt(totalReceipts), color:"var(--accent)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{marginBottom:12,padding:"10px 14px",background:"var(--info-l)",borderRadius:8,fontSize:12,color:"var(--info)",display:"flex",gap:16,alignItems:"center"}}>
                <span>As of date: <strong>{asOfDate}</strong></span>
                <span style={{color:"var(--ink3)"}}>If a customer already has an opening balance, it will be updated with these new values.</span>
              </div>

              <UnresolvedCustomersPanel
                groups={groupUnresolvedByName(preview.filter(r=>r.noMatch),"name")}
                customers={customers}
                onResolve={resolveByName}
                onCustomerCreated={cust=>setCustomers(cs=>[...cs,cust])}
                toast={toast}
              />

              <div style={{maxHeight:380,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Name in file</th>
                      <th style={S.th()}>Matched to</th>
                      <th style={S.th(true)}>Sale amount</th>
                      <th style={S.th(true)}>Received</th>
                      <th style={S.th(true)}>Balance</th>
                      <th style={S.th()}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0,200).map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)",
                        background:r.noMatch?"#fff8f0":"#fff"}}>
                        <td style={{...S.td,fontWeight:500,fontSize:12}}>{r.name}</td>
                        <td style={{...S.td,fontSize:12}}>
                          {r.matchedName
                            ? <span style={{color:"var(--accent)",fontWeight:500}}>{r.matchedName}</span>
                            : <span style={{color:"var(--warn)"}}>⚠ No match</span>}
                        </td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:"var(--info)"}}>{fmt(r.sales)}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:"var(--accent)"}}>{fmt(r.receipts)}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,fontSize:12,
                          color:r.balance>0?"var(--danger)":r.balance<0?"var(--accent)":"var(--ink3)"}}>
                          {fmt(Math.abs(r.balance))}{r.balance<0?" Cr":r.balance>0?" Dr":""}
                        </td>
                        <td style={S.td}>
                          {r.noMatch
                            ? <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:"var(--warn-l)",color:"var(--warn)"}}>No match</span>
                            : <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:"var(--accent-l)",color:"var(--accent)"}}>✓ Ready</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
      {preview.length>200&&(
        <div style={{padding:"10px 14px",textAlign:"center",fontSize:12,color:"var(--ink3)",borderTop:"1px solid var(--paper2)"}}>
          Showing first 200 of {preview.length} rows — all {preview.filter(r=>r.customerId).length} matched rows will be uploaded
        </div>
      )}
              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(2)}>← Back</button>
                <button style={S.btnPrimary} onClick={upload} disabled={saving||validCount===0}>
                  {saving?"Uploading…":`💾 Save ${validCount} opening balances`}
                </button>
              </div>
            </div>
          )}

          {/* Step 4 — Result */}
          {step===4&&result&&(
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>{result.errors.length===0?"✅":"⚠️"}</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>Upload complete</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20,maxWidth:420,margin:"0 auto 20px"}}>
                {[
                  {label:"Saved",      value:result.upserted,  color:"var(--accent)"},
                  {label:"No match",   value:result.skipped,   color:"var(--warn)"},
                  {label:"Errors",     value:result.errors.length, color:"var(--danger)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:25,fontWeight:300,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {result.unmatchedRows?.length>0&&(
                <div style={{textAlign:"left",marginBottom:16,padding:"12px 16px",background:"var(--warn-l)",borderRadius:8,maxHeight:140,overflowY:"auto"}}>
                  <div style={{fontSize:12,fontWeight:500,color:"var(--warn)",marginBottom:6}}>These customers were not found — add them first then re-upload:</div>
                  {[...new Set(result.unmatchedRows.map(r=>r.name))].map((n,i)=>(
                    <div key={i} style={{fontSize:12,color:"var(--warn)",marginBottom:2}}>· {n}</div>
                  ))}
                </div>
              )}
              {result.errors.length>0&&(
                <div style={{textAlign:"left",marginBottom:16,padding:"12px 16px",background:"var(--danger-l)",borderRadius:8,maxHeight:120,overflowY:"auto"}}>
                  {result.errors.map((e,i)=><div key={i} style={{fontSize:12,color:"var(--danger)",marginBottom:2}}>{e}</div>)}
                </div>
              )}
              <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:16}}>
                <button style={S.btnPrimary} onClick={onSave}>✓ Done</button>
                {result.skipped>0&&<button style={S.btnGhost} onClick={()=>setStep(1)}>Upload again</button>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// ── OPENING BALANCE MODAL ─────────────────────────────────────
function OpeningBalanceModal({ customer, existing, onClose, onSave }) {
  const [asOfDate, setAsOfDate]       = useState(existing?.as_of_date || new Date().toISOString().slice(0,10));
  const [openingSales, setOpeningSales]       = useState(existing?.opening_sales || "");
  const [openingReceipts, setOpeningReceipts] = useState(existing?.opening_receipts || "");
  const [notes, setNotes]             = useState(existing?.notes || "");
  const [saving, setSaving]           = useState(false);

  const save = async () => {
    if (!asOfDate) return alert("Please select an as-of date.");
    setSaving(true);
    try {
      const payload = {
        customer_id:      customer.id,
        as_of_date:       asOfDate,
        opening_sales:    parseFloat(openingSales)||0,
        opening_receipts: parseFloat(openingReceipts)||0,
        notes:            notes||null,
        updated_at:       new Date().toISOString(),
      };
      const { error } = await supabase.from("customer_opening_balances")
        .upsert(payload, { onConflict:"customer_id" });
      if (error) throw error;
      onSave();
    } catch(e) { alert("Error: "+e.message); }
    finally { setSaving(false); }
  };

  const balance = (parseFloat(openingSales)||0) - (parseFloat(openingReceipts)||0);

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:480}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={S.pageTitle}>Opening balance</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>{customer.name}</div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          {existing && (
            <div style={{marginBottom:16,padding:"10px 14px",background:"var(--info-l)",borderRadius:8,fontSize:12,color:"var(--info)"}}>
              ✓ Existing opening balance found — saving will update it.
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr",gap:14}}>
            <div>
              <label style={S.label}>As of date * (date you are starting to use this system)</label>
              <input type="date" value={asOfDate} onChange={e=>setAsOfDate(e.target.value)} style={S.input}/>
            </div>
            <div>
              <label style={S.label}>Total sales up to this date (₹)</label>
              <input type="number" min="0" step="0.01" value={openingSales} onChange={e=>setOpeningSales(e.target.value)}
                placeholder="0.00" style={S.input}/>
              <div style={{fontSize:11,color:"var(--ink3)",marginTop:3}}>Total amount this customer owes from before you started using this system</div>
            </div>
            <div>
              <label style={S.label}>Total receipts up to this date (₹)</label>
              <input type="number" min="0" step="0.01" value={openingReceipts} onChange={e=>setOpeningReceipts(e.target.value)}
                placeholder="0.00" style={S.input}/>
              <div style={{fontSize:11,color:"var(--ink3)",marginTop:3}}>Total amount already paid by this customer before you started using this system</div>
            </div>
            <div>
              <label style={S.label}>Notes (optional)</label>
              <input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. Migrated from Tally on 01-Apr-2026" style={S.input}/>
            </div>
          </div>

          {/* Live balance preview */}
          <div style={{marginTop:16,padding:"14px 16px",background:"var(--paper)",borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:12,color:"var(--ink3)"}}>Opening balance due</div>
            <div style={{fontFamily:"var(--mono)",fontWeight:500,fontSize:16,color:balance>0?"var(--danger)":balance<0?"var(--accent)":"var(--ink3)"}}>
              {fmt(Math.abs(balance))}{balance<0?" Cr":balance>0?" Dr":""}
            </div>
          </div>

          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:16,borderTop:"1px solid var(--paper2)"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>
              {saving?"Saving…":"💾 Save opening balance"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── DAILY UPLOAD MODAL ────────────────────────────────────────
function DailyUploadModal({ onClose, onSave, toast }) {
  const [step, setStep]         = useState(1); // 1=upload, 2=preview, 3=result
  const [preview, setPreview]   = useState([]);
  const [result, setResult]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [customers, setCustomers] = useState([]);
  const fileRef = useRef(null);

useEffect(() => {
  const loadAll = async () => {
    let all = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const { data, error } = await supabase.from("customers")
        .select("id,name,phone")
        .eq("is_active", true)
        .range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      all = [...all, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    setCustomers(all);
  };
  loadAll();
}, []);

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      let text = e.target.result;

      // Handle Excel
      if (file.name.endsWith(".xlsx")||file.name.endsWith(".xls")) {
        if (!window.XLSX) {
          await new Promise((res,rej)=>{
            const s=document.createElement("script");
            s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            s.onload=res;s.onerror=rej;document.head.appendChild(s);
          });
        }
        const data = new Uint8Array(e.target.result);
        const wb = window.XLSX.read(data,{type:"array"});
        const ws = wb.Sheets[wb.SheetNames[0]];
        text = window.XLSX.utils.sheet_to_csv(ws);
      }

      const { headers, rows } = parseCSV(text);

      // Map to expected columns — Date, Order_No, Name, Work Type, Amount
      const dateCol   = headers.find(h => /date/i.test(h)) || headers[0];
      const orderCol  = headers.find(h => /order/i.test(h));
      const nameCol   = headers.find(h => /name|customer/i.test(h));
      const typeCol   = headers.find(h => /type|work/i.test(h));
      const amtCol    = headers.find(h => /amount|amt|total/i.test(h));

      const matchCustomer = makeExactMatcher(customers);
      const parsed = rows.map(row => {
        const rawDate  = dateCol  ? row[dateCol]  : "";
        const isoDate  = parseDate(rawDate);
        const custName = nameCol  ? row[nameCol]  : "";
        const amount   = parseFloat((amtCol ? row[amtCol] : "").replace(/[,₹\s]/g,"")) || 0;
        const orderNo  = orderCol ? row[orderCol] : "";
        const workType = typeCol  ? row[typeCol]  : "";
        const customer = matchCustomer(custName);

        return {
          rawDate, isoDate, custName, amount, orderNo, workType,
          customer,
          matchedName: customer?.name || null,
          customerId:  customer?.id   || null,
          valid:       !!(isoDate && amount > 0 && custName),
          noMatch:     !customer && !!custName,
        };
      }).filter(r => r.rawDate || r.custName);

      setPreview(parsed);
      setStep(2);
    };

    if (file.name.endsWith(".xlsx")||file.name.endsWith(".xls")) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const resolveByName = (name, cust) => {
    setPreview(p => p.map(r => r.custName===name
      ? { ...r, customer:cust, matchedName:cust.name, customerId:cust.id, noMatch:false }
      : r));
  };

  const uploadRows = async () => {
    setSaving(true);
    let inserted=0, skipped=0, unmatched=0, errors=[];

    const validRows = preview.filter(r => r.valid && r.customerId);

    for (const row of validRows) {
      try {
        // Duplicate check: same date + customer + amount
        const { data: existing } = await supabase.from("historical_transactions")
          .select("id")
          .eq("customer_id", row.customerId)
          .eq("txn_date", row.isoDate)
          .eq("amount", row.amount)
          .eq("txn_type", "sale")
          .limit(1);

        if (existing && existing.length > 0) { skipped++; continue; }

        const { error } = await supabase.from("historical_transactions").insert({
          customer_id:   row.customerId,
          customer_name: row.custName,
          txn_date:      row.isoDate,
          txn_type:      "sale",
          amount:        row.amount,
          reference:     row.orderNo || null,
          work_type:     row.workType || null,
          order_no:      row.orderNo || null,
          source:        "daily_upload",
        });
        if (error) throw error;
        inserted++;
      } catch(e) { errors.push(`${row.custName} (${row.isoDate}): ${e.message}`); }
    }

    unmatched = preview.filter(r => r.valid && !r.customerId).length;

    setSaving(false);
    setResult({ inserted, skipped, unmatched, errors,
      unmatchedRows: preview.filter(r => r.valid && !r.customerId) });
    setStep(3);
  };

  const validCount    = preview.filter(r=>r.valid&&r.customerId).length;
  const unmatchedCount = preview.filter(r=>r.valid&&!r.customerId).length;
  const invalidCount  = preview.filter(r=>!r.valid).length;

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:820,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Daily sales upload</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>
              {["","Upload file","Preview & confirm","Done"][step]}
            </div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>

        <div style={{padding:"20px 28px 28px"}}>

          {/* Step 1 — Upload */}
          {step===1&&(
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--info-l)",borderRadius:8,fontSize:13,color:"var(--info)"}}>
                Expected columns: <strong>Date · Order_No · Name · Work Type · Amount</strong>
                <div style={{marginTop:4,fontSize:12}}>Supports CSV, tab-delimited and Excel files. Duplicate rows (same date + customer + amount) are automatically skipped.</div>
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>Click to select file or drag and drop</div>
                <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>CSV, TSV or Excel (.xlsx)</div>
                <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{display:"none"}}
                  onChange={e=>parseFile(e.target.files[0])}/>
              </div>
              <div style={{marginTop:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                <strong>Sample row from your file:</strong><br/>
                20/04/2026 &nbsp;|&nbsp; O-AD-6042 &nbsp;|&nbsp; TRIDENT STUDIOS LLP &nbsp;|&nbsp; PHOTO BOOK &nbsp;|&nbsp; 1674
              </div>
            </div>
          )}

          {/* Step 2 — Preview */}
          {step===2&&(
            <div>
              {/* Summary */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
                {[
                  {label:"Total rows",    value:preview.length,   color:"var(--info)"},
                  {label:"Ready to import", value:validCount,     color:"var(--accent)"},
                  {label:"No customer match", value:unmatchedCount, color:"var(--warn)"},
                  {label:"Invalid rows",  value:invalidCount,     color:"var(--danger)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>

              <UnresolvedCustomersPanel
                groups={groupUnresolvedByName(preview.filter(r=>r.valid&&r.noMatch),"custName")}
                customers={customers}
                onResolve={resolveByName}
                onCustomerCreated={cust=>setCustomers(cs=>[...cs,cust])}
                toast={toast}
              />

              <div style={{maxHeight:420,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th>
                      <th style={S.th()}>Order no.</th>
                      <th style={S.th()}>Customer (CSV)</th>
                      <th style={S.th()}>Matched to</th>
                      <th style={S.th()}>Work type</th>
                      <th style={S.th(true)}>Amount</th>
                      <th style={S.th()}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)",
                        background:!r.valid?"var(--paper)":r.noMatch?"#fff8f0":"#fff"}}>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{r.isoDate||r.rawDate}</td>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>{r.orderNo||"—"}</td>
                        <td style={{...S.td,fontWeight:500,fontSize:12}}>{r.custName||"—"}</td>
                        <td style={{...S.td,fontSize:12}}>
                          {r.matchedName
                            ? <span style={{color:"var(--accent)",fontWeight:500}}>{r.matchedName}</span>
                            : <span style={{color:"var(--warn)"}}>⚠ No match</span>}
                        </td>
                        <td style={{...S.td,fontSize:12,color:"var(--ink3)"}}>{r.workType||"—"}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{r.amount>0?fmt(r.amount):"—"}</td>
                        <td style={S.td}>
                          {!r.valid
                            ? <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:"var(--paper2)",color:"var(--ink3)"}}>Skip</span>
                            : r.noMatch
                              ? <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:"var(--warn-l)",color:"var(--warn)"}}>No match</span>
                              : <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:"var(--accent-l)",color:"var(--accent)"}}>✓ Ready</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Re-upload</button>
                <button style={S.btnPrimary} onClick={uploadRows}
                  disabled={saving||validCount===0}>
                  {saving?<span style={{display:"inline-block",width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>:"⬆"}
                  {saving?"Uploading…":`Import ${validCount} rows`}
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — Result */}
          {step===3&&result&&(
            <div style={{textAlign:"center",padding:"10px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>{result.errors.length===0?"✅":"⚠️"}</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>
                Upload complete
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20,maxWidth:560,margin:"0 auto 20px"}}>
                {[
                  {label:"Inserted",    value:result.inserted,   color:"var(--accent)"},
                  {label:"Skipped (dup)",value:result.skipped,   color:"var(--info)"},
                  {label:"No match",    value:result.unmatched,  color:"var(--warn)"},
                  {label:"Errors",      value:result.errors.length, color:"var(--danger)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:25,fontWeight:300,lineHeight:1,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6,letterSpacing:"0.5px"}}>{s.label}</div>
                  </div>
                ))}
              </div>

              {result.unmatchedRows&&result.unmatchedRows.length>0&&(
                <div style={{textAlign:"left",marginBottom:16,padding:"12px 16px",background:"var(--warn-l)",borderRadius:8,maxHeight:150,overflowY:"auto"}}>
                  <div style={{fontSize:12,fontWeight:500,color:"var(--warn)",marginBottom:6}}>Unmatched customer names — add these customers and re-upload:</div>
                  {[...new Set(result.unmatchedRows.map(r=>r.custName))].map((n,i)=>(
                    <div key={i} style={{fontSize:12,color:"var(--warn)",marginBottom:2}}>· {n}</div>
                  ))}
                </div>
              )}

              {result.errors.length>0&&(
                <div style={{textAlign:"left",marginBottom:16,padding:"12px 16px",background:"var(--danger-l)",borderRadius:8,maxHeight:120,overflowY:"auto"}}>
                  {result.errors.map((e,i)=><div key={i} style={{fontSize:12,color:"var(--danger)",marginBottom:2}}>{e}</div>)}
                </div>
              )}

              <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:16}}>
                <button style={S.btnPrimary} onClick={onSave}>✓ Done</button>
                {result.unmatched>0&&<button style={S.btnGhost} onClick={()=>setStep(1)}>Upload again</button>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── MAIN HISTORICAL IMPORT PAGE ───────────────────────────────
export function HistoricalImportPage({ toast }) {
  const [customers, setCustomers]         = useState([]);
  const [openingBalances, setOpeningBalances] = useState({});
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState("");
  const [obModal, setObModal]             = useState(null); // customer object
  const [dailyModal, setDailyModal]       = useState(false);
const [bulkOBModal, setBulkOBModal]     = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [historyData, setHistoryData]     = useState(null);
  const [histLoading, setHistLoading]     = useState(false);
  const [activeTab, setActiveTab]         = useState("balances"); // balances | daily | history

const load = useCallback(async () => {
  setLoading(true);
  // Load all customers with pagination
  let allCustomers = [];
  let from = 0;
  const pageSize = 1000;
  while (true) {
    const { data, error } = await supabase.from("customers")
      .select("id,name,type,phone")
      .eq("is_active", true)
      .order("name")
      .range(from, from + pageSize - 1);
    if (error || !data || data.length === 0) break;
    allCustomers = [...allCustomers, ...data];
    if (data.length < pageSize) break;
    from += pageSize;
  }
  // Load opening balances (paged — Supabase caps a single request at 1000 rows)
  let obRes = [];
  from = 0;
  while (true) {
    const { data, error } = await supabase.from("customer_opening_balances")
      .select("*").order("customer_id").range(from, from + pageSize - 1);
    if (error || !data || data.length === 0) break;
    obRes = [...obRes, ...data];
    if (data.length < pageSize) break;
    from += pageSize;
  }
  const obMap = {};
  obRes.forEach(ob => { obMap[ob.customer_id] = ob; });
  setCustomers(allCustomers);
  setOpeningBalances(obMap);
  setLoading(false);
}, []);

  useEffect(()=>{ load(); },[load]);

  const loadCustomerHistory = async (cust) => {
    setHistoryCustomer(cust);
    setHistLoading(true);
    const { data } = await supabase.from("historical_transactions")
      .select("*").eq("customer_id",cust.id)
      .order("txn_date",{ascending:false});
    setHistoryData(data||[]);
    setHistLoading(false);
  };

  const deleteTransaction = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    await supabase.from("historical_transactions").delete().eq("id",id);
    toast("Deleted.","error");
    loadCustomerHistory(historyCustomer);
  };

  const deleteOpeningBalance = async (customerId) => {
    if (!window.confirm("Remove opening balance for this customer?")) return;
    await supabase.from("customer_opening_balances").delete().eq("customer_id",customerId);
    toast("Opening balance removed.","error");
    load();
  };

  const filtered = customers.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalOpeningSales    = Object.values(openingBalances).reduce((s,ob)=>s+parseFloat(ob.opening_sales||0),0);
  const totalOpeningReceipts = Object.values(openingBalances).reduce((s,ob)=>s+parseFloat(ob.opening_receipts||0),0);
  const customersWithOB      = Object.keys(openingBalances).length;

  // Daily upload summary
  const [dailySummary, setDailySummary] = useState({total:0,sales:0});
  useEffect(()=>{
    supabase.from("historical_transactions").select("amount",{count:"exact"}).eq("source","daily_upload")
      .then(({data,count})=>{
        const total = (data||[]).reduce((s,r)=>s+parseFloat(r.amount||0),0);
        setDailySummary({total,sales:count||0});
      });
  },[]);

  return (
    <div>
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Historical Data</div>
          <div style={S.subtitle}>Opening balances · Daily sales upload · Transaction history</div>
        </div>
        <div style={{display:"flex",gap:8}}>
  <button style={S.btnGhost} onClick={()=>setBulkOBModal(true)}>⬆ Bulk opening balances</button>
  
</div>
      </div>

      {/* Summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:28}}>
        {[
          {label:"Customers with opening balance", value:customersWithOB,             color:"var(--info)"},
          {label:"Total opening sales",            value:fmt(totalOpeningSales),       color:"var(--warn)"},
          {label:"Total opening receipts",         value:fmt(totalOpeningReceipts),    color:"var(--accent)"},
          {label:"Daily upload transactions",      value:dailySummary.sales,           color:"#6a1b9a"},
        ].map(s=>(
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:"1px solid var(--paper3)"}}>
        {[
          {key:"balances", label:"Opening balances"},
          {key:"history",  label:"Upload history"},
        ].map(t=>(
          <button key={t.key} onClick={()=>setActiveTab(t.key)}
            style={{padding:"10px 20px",fontSize:13,cursor:"pointer",border:"none",background:"transparent",fontFamily:"var(--mono)",
              borderBottom:`2px solid ${activeTab===t.key?"var(--accent)":"transparent"}`,
              color:activeTab===t.key?"var(--accent)":"var(--ink3)",fontWeight:activeTab===t.key?500:400}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: Opening balances */}
      {activeTab==="balances"&&(
        <div>
          <div style={{marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
            <div style={{position:"relative",flex:1,maxWidth:300}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
              <input placeholder="Search customer…" value={search} onChange={e=>setSearch(e.target.value)}
                style={{...S.input,paddingLeft:34,background:"#fff"}}/>
            </div>
            <span style={{fontSize:12,color:"var(--ink3)"}}>{filtered.length} customer{filtered.length!==1?"s":""}</span>
          </div>

          <div style={S.card}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                <th style={S.th()}>Customer</th>
                <th style={S.th()}>Type</th>
                <th style={S.th()}>As of date</th>
                <th style={S.th(true)}>Opening sales</th>
                <th style={S.th(true)}>Opening receipts</th>
                <th style={S.th(true)}>Opening balance</th>
                <th style={S.th()}></th>
              </tr></thead>
              <tbody>
                {loading&&<tr><td colSpan={7} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</td></tr>}
                {!loading&&filtered.map(c=>{
                  const ob = openingBalances[c.id];
                  const bal = ob ? parseFloat(ob.opening_sales||0)-parseFloat(ob.opening_receipts||0) : null;
                  return (
                    <tr key={c.id} style={{borderBottom:"1px solid var(--paper2)"}}>
                      <td style={{...S.td,fontWeight:500}}>{c.name}</td>
                      <td style={S.td}>
                        <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,
                          background:c.type==="outstation"?"var(--warn-l)":c.type==="tpf"?"var(--info-l)":c.type==="prv"?"#f3e8ff":"var(--accent-l)",
                          color:c.type==="outstation"?"var(--warn)":c.type==="tpf"?"var(--info)":c.type==="prv"?"#6a1b9a":"var(--accent)"}}>
                          {c.type}
                        </span>
                      </td>
                      <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>{fmtDMY(ob?.as_of_date)||"—"}</td>
                      <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:"var(--info)"}}>{ob?fmt(ob.opening_sales):"—"}</td>
                      <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12,color:"var(--accent)"}}>{ob?fmt(ob.opening_receipts):"—"}</td>
                      <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:bal>0?"var(--danger)":bal<0?"var(--accent)":"var(--ink3)"}}>
                        {bal!==null?`${fmt(Math.abs(bal))}${bal<0?" Cr":bal>0?" Dr":""}` : "—"}
                      </td>
                      <td style={S.td}>
                        <div style={{display:"flex",gap:6}}>
                          <button style={S.btnSm} onClick={()=>setObModal(c)}>
                            {ob?"Edit":"+ Set"}
                          </button>
                          {ob&&<button style={{...S.btnSm,color:"var(--danger)",borderColor:"var(--danger-l)"}} onClick={()=>deleteOpeningBalance(c.id)}>✕</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Upload history */}
      {activeTab==="history"&&(
        <div style={{display:"grid",gridTemplateColumns:historyCustomer?"1fr 1.5fr":"1fr",gap:20}}>
          {/* Customer list */}
          <div>
            <div style={{marginBottom:12,position:"relative"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
              <input placeholder="Search customer…" value={search} onChange={e=>setSearch(e.target.value)}
                style={{...S.input,paddingLeft:34,background:"#fff"}}/>
            </div>
            <div style={S.card}>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                  <th style={S.th()}>Customer</th>
                  <th style={S.th(true)}>Transactions</th>
                </tr></thead>
                <tbody>
                  {loading&&<tr><td colSpan={2} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</td></tr>}
                  {!loading&&filtered.map(c=>(
                    <tr key={c.id} onClick={()=>loadCustomerHistory(c)}
                      style={{borderBottom:"1px solid var(--paper2)",cursor:"pointer",
                        background:historyCustomer?.id===c.id?"var(--accent-l)":"transparent",transition:"background 0.1s"}}>
                      <td style={{...S.td,fontWeight:500}}>{c.name}<div style={{fontSize:11,color:"var(--ink3)"}}>{c.type}</div></td>
                      <td style={{...S.td,textAlign:"right",fontSize:12,color:"var(--ink3)"}}>click to view</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Transaction detail */}
          {historyCustomer&&(
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                <div>
                  <div style={{fontWeight:500,fontSize:15}}>{historyCustomer.name}</div>
                  <div style={{fontSize:12,color:"var(--ink3)"}}>{historyData?.length||0} historical transactions</div>
                </div>
                <button style={S.btnSm} onClick={()=>setHistoryCustomer(null)}>✕</button>
              </div>

              {histLoading&&<div style={{textAlign:"center",padding:30,color:"var(--ink3)"}}>Loading…</div>}

              {!histLoading&&historyData&&(
                <div style={{...S.card,maxHeight:520,overflowY:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead style={{position:"sticky",top:0}}>
                      <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                        <th style={S.th()}>Date</th>
                        <th style={S.th()}>Order no.</th>
                        <th style={S.th()}>Work type</th>
                        <th style={S.th()}>Source</th>
                        <th style={S.th(true)}>Amount</th>
                        <th style={S.th()}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.length===0&&(
                        <tr><td colSpan={6} style={{padding:30,textAlign:"center",color:"var(--ink3)",fontSize:13}}>
                          No historical transactions for this customer.
                        </td></tr>
                      )}
                      {historyData.map((r,i)=>(
                        <tr key={r.id||i} style={{borderBottom:"1px solid var(--paper2)"}}>
                          <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{fmtDMY(r.txn_date)}</td>
                          <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>{r.order_no||"—"}</td>
                          <td style={{...S.td,fontSize:12}}>{r.work_type||"—"}</td>
                          <td style={S.td}>
                            <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,
                              background:r.source==="daily_upload"?"var(--info-l)":"var(--paper2)",
                              color:r.source==="daily_upload"?"var(--info)":"var(--ink3)"}}>
                              {r.source==="daily_upload"?"Daily upload":"Manual"}
                            </span>
                          </td>
                          <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--info)"}}>{fmt(r.amount)}</td>
                          <td style={S.td}>
                            <button style={{...S.btnSm,color:"var(--danger)",borderColor:"var(--danger-l)",fontSize:11}}
                              onClick={()=>deleteTransaction(r.id)}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {historyData.length>0&&(
                      <tfoot>
                        <tr style={{background:"var(--paper2)",borderTop:"2px solid var(--paper3)"}}>
                          <td colSpan={4} style={{...S.td,fontWeight:500,color:"var(--ink3)"}}>Total ({historyData.length} rows)</td>
                          <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:"var(--info)"}}>
                            {fmt(historyData.reduce((s,r)=>s+parseFloat(r.amount||0),0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {obModal&&(
        <OpeningBalanceModal
          customer={obModal}
          existing={openingBalances[obModal.id]||null}
          onClose={()=>setObModal(null)}
          onSave={()=>{ setObModal(null); load(); toast("Opening balance saved!"); }}
        />
      )}
      {dailyModal&&(
        <DailyUploadModal
          onClose={()=>setDailyModal(false)}
          onSave={()=>{ setDailyModal(false); load(); toast("Upload complete!","success"); }}
          toast={toast}
        />
      )}
{bulkOBModal&&(
  <BulkOpeningBalanceModal
    onClose={()=>setBulkOBModal(false)}
    onSave={()=>{ setBulkOBModal(false); load(); toast("Opening balances saved!","success"); }}
    toast={toast}
  />
)}
    </div>
  );
}
